import { Builder, builder, BuilderComponent } from '@builder.io/react'
import { useRouter } from 'next/router'
import { LoadingIndicator } from '../../components/LoadingIndicator'
import Head from 'next/head'
import NotFoundPage from '../404'
import React from 'react'
import { chain, concat, filter, find, has, map, pipe, prop, uniq } from 'ramda'
import { paramCase } from 'param-case'
import {
  getCars,
  getCities,
  getDataForMinorCity,
  getMinorCitiesFromCityContent,
  isMajorCity,
} from '../../asyncLoaders/buttercms'
import { getReviewIODataFromNest } from '../../asyncLoaders/nest'
import minorCityData from '../../data/minorCitiesArray.json'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { MajorCityPageTemplate } from '../../components/CityPage/MajorCityPageTemplate'
import MinorCityPageTemplate from '../../components/CityPage/MinorCityPageTemplate'
import { CityData } from '../../components/CityPage'
import '../../components/Builder/CodeComponentsRegistration'

builder.init(process.env.BUILDER_API_KEY)

type CityPostProps = {
  slug: string
  isMajorCity: boolean
  city?: CityData
  cities?: CityData[]
  cityName?: string
  state?: string
  rating: string
  reviewCount: number
  yelpData: any
  carsContent?: any[]
}

const DynamicCityPage = (props) => {
  const router = useRouter()

  const { builderCities, cities, butterCities, rating, reviewCount } = props

  console.log("[Builder Props]", { builderCities, cities, butterCities, rating, reviewCount })

  const isBuilder = !!(builderCities || Builder.isEditing || Builder.isPreviewing);
  const notBulder = !!props.city;

  console.log("[Builder EVAL]", { isBuilder, notBulder });

  if (router.isFallback) {
    return <LoadingIndicator />
  }
  // Makes it so only the published city pages can be viewed here
  if (builderCities || Builder.isEditing || Builder.isPreviewing) {
    return (
      <div>
        <Head>
          <title>Brake Repair Near You From NuBrakes</title>
        </Head>
        <BuilderComponent
          model={'cities'}
          content={cities}
          data={{ butterCities, builderCities, rating: 2.2, reviewCount }}
        />
      </div>
    )
  } else if (props.city) {
    const { city, isMajorCity, cities, rating, reviewCount, carsContent } = props
    return (
      <>
        <CityPost
          city={city}
          slug={city.slug}
          isMajorCity={isMajorCity}
          cities={cities}
          cityName={city.cityName}
          state={city.stateName}
          rating={rating}
          reviewCount={reviewCount}
          yelpData={{}} //TODO REVIEW HOW TO USE THIS
          carsContent={carsContent}
        />
      </>
    )
  } else {
    return <NotFoundPage />
  }
}

const CityPost: NextPage<CityPostProps> = (props) => {
  const { rating, reviewCount, carsContent } = props
  if (props.isMajorCity) {
    const {
      cityName,
      stateAbbreviation,
      backgroundImage,
      listOfZips,
      listOfMinorCities,
      phoneNumber,
      tags,
      schemas,
      titleTagTitle,
      metaDescription,
      reviews,
      blobSection,
      blobParagraphs,
    } = props.city
    return (
      <MajorCityPageTemplate
        cities={props.cities}
        phoneNumber={`${phoneNumber}`}
        titleTagTitle={titleTagTitle}
        metaDescription={metaDescription}
        schemas={schemas}
        rating={rating}
        blobSection={blobSection}
        blobParagraphs={blobParagraphs}
        reviewCount={reviewCount}
        cityName={cityName}
        stateAbbreviation={stateAbbreviation}
        backgroundImage={backgroundImage}
        listOfZips={listOfZips}
        reviews={reviews}
        listOfMinorCities={listOfMinorCities}
        tags={tags}
        carsContent={carsContent}
      />
    )
  }
  return (
    <MinorCityPageTemplate
      cityName={props.cityName}
      state={props.state}
      rating={props.rating}
      reviewCount={props.reviewCount}
      yelpData={{}}
      cities={props.cities}
    />
  )
}
export const getStaticProps: GetStaticProps = async (context) => {
  // TODO: REFACTOR TO ALLOW BUILDER CITIES ONLY AND NOT BUTTERCMS
  // TODO: Heavy Simplification Necessary Here
  const butterCities: any[] = await getCities()
  const { rating, reviewCount } = await getReviewIODataFromNest()
  // This could be that it is a builder city also
  const majorCityBool = isMajorCity(context?.params?.cities[0], butterCities)
  // Don't target on url and device for better cache efficiency
  const targeting = { urlPath: '_', device: '_' } as any
  const path = `/cities/${(context.params?.cities as string[])?.join('/') || ''}`

  const defaultPropOptions = {
    userAttributes: {
      ...targeting,
      urlPath: path,
    },
  }

  const cars = (await getCars()) || []

  const results = (await builder.get('cities', { ...defaultPropOptions }).promise()) || null

  const getYelpDataForCityName = (cityName) => {
    return pipe(
      find((val: any) => prop('location', val) === cityName),
      prop('businesses')
    )(minorCityData)
  }

  const getMinorCityInfoBySlug = async (slug: string) => {
    return pipe(
      filter<any[], any>(has('listOfMinorCities')),
      chain((city: any) => {
        const minorCities = prop('listOfMinorCities', city)
        return getDataForMinorCity(city, minorCities)
      }),
      find((val: any) => {
        return `${paramCase(val.cityName)}-brake-repair` === slug
      })
    )(butterCities)
  }
  // There are results returned from Builder.io Lets use those
  // WARNING! BUILDER WILL NOT RETURN RESULTS FOR UNPUBLISHED PAGES!
  if (results) {
    return {
      props: {
        cities: results,
        isMajorCity: false,
        butterCities: butterCities,
        builderCities: true,
        reviewCount,
        carsContent: cars,
        rating,
      },
      // Revalidate Page Changes after 1 Hour
      revalidate: 3600,
    }
  }
  // Let's see if this url param exists in the list of cities from butterCMS
  const city = butterCities.find((c) => prop('slug', c) === context?.params?.cities[0])
  // It is a major city and has a city document attached
  if (majorCityBool && city) {
    return {
      props: {
        city,
        cities: butterCities,
        isMajorCity: true,
        rating,
        reviewCount,
        builderCities: false,
        carsContent: cars,
      },
      revalidate: 60 * 60 * 24,
    }
    // Is not a major city but has a city document (has to be minor then)
  } else if (!majorCityBool && city) {
    const result = await getMinorCityInfoBySlug(context?.params?.cities[0])
    return {
      props: pipe(
        JSON.stringify,
        JSON.parse
      )({
        cityName: result.cityName,
        phoneNumber: result.phoneNumber,
        state: result.state,
        cities: butterCities,
        yelpData: getYelpDataForCityName(result.cityName),
        isMajorCity: false,
        rating,
        reviewCount,
        builderCities: false,
        carsContent: cars,
      }),
      revalidate: 60 * 60 * 24,
    }
  }
  // No Builder.io Path or ButterCMS Path so 404
  return {
    props: {
      notFound: true,
    },
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const defaultPathOptions = {
    key: `cities:all`,
    fields: 'data.url',
    limit: 200,
    options: {
      noTargeting: true,
    },
  }
  const builderResults = await builder.getAll('cities', defaultPathOptions)
  const cityContent: any[] = await getCities()
  const cityToSlug = (city) => ({ data: { url: `/cities/${city.slug}` } })
  const majorCityPaths = cityContent.map(cityToSlug)
  const allPaths = await pipe(
    getMinorCitiesFromCityContent,
    map((minorCity) => `${paramCase(minorCity)}-brake-repair`),
    map((cityPath) => ({ data: { url: `/cities/${cityPath}` } })),
    concat(majorCityPaths),
    concat(builderResults),
    uniq
  )(cityContent)

  const paths = allPaths
    .filter((item) => item.data?.url?.startsWith('/cities/'))
    .map((item) => ({
      params: { cities: (item.data?.url?.replace('/cities/', '') || '_').split('/') },
    }))

  return {
    paths: paths,
    fallback: true,
  }
}

export default DynamicCityPage
